import React, {useEffect, useRef} from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {useIsScreenLargerThan} from 'app/general/mediaqueries';
import {EmbedlyVideoThumbnail} from 'app/general/widgets/EmbedlyVideoThumbnail';
import {
  CAROUSEL_PADDING,
  CAROUSEL_THUMBNAIL_SIZE,
} from 'app/general/widgets/ImagesCarousel/CarouselThumbnail';
import {
  isAttachedMedia,
  isEmbeddedMedia,
} from 'app/general/widgets/ImagesCarousel/utils';
import {isMediaImage, isMediaVideo} from 'app/mediaaccess/utils';
import {StyledDiffedImageContainerDiv} from 'app/procedures/styles';
import {useAccountSettingsState} from 'app/settings/api';
import {gt} from 'app/utils/i18n';
import {markAsSafe, safeCdnImageRef} from 'app/utils/strings';

import type {AttachedMediaWithChangedStateAndEmbed} from 'app/general/widgets/ImagesCarousel/types';

interface Props {
  image: AttachedMediaWithChangedStateAndEmbed;
  tabIndex?: number;
  slideId: number;
  currentSlideId: number;
  numberOfShownSlides: number;
}

export function CarouselPreviewElement({
  slideId,
  currentSlideId,
  image,
  tabIndex,
  numberOfShownSlides,
}: Props) {
  const isXLScreen = useIsScreenLargerThan('xl');
  const {MEDIA_URL_READ} = useAccountSettingsState().data!;
  const iframeRef = useRef<HTMLDivElement | null>(null);

  useEffect(function resizeIframe() {
    const iframe = iframeRef.current?.querySelector('iframe');
    if (iframe) {
      const w = isNaN(Number(iframe.width)) ? 0 : parseInt(iframe.width);
      const h = isNaN(Number(iframe.height)) ? 0 : parseInt(iframe.height);

      const safeW =
        Number.isFinite(w) && w > 0
          ? w
          : Math.round(iframe.getBoundingClientRect().width || 640);
      const safeH =
        Number.isFinite(h) && h > 0
          ? h
          : Math.round(iframe.getBoundingClientRect().height || 360);

      const parentWidth = iframe.getBoundingClientRect().width || 640;
      iframe.width = parentWidth.toString();
      iframe.height = ((safeH / safeW) * parentWidth).toString();
    }
  }, []);

  if (isEmbeddedMedia(image)) {
    const className = 'changedState' in image ? image.changedState || '' : '';

    if (isAttachedMedia(image)) {
      if (isMediaImage(image)) {
        const imageURL = image.image_ref;
        return (
          <StyledImageContainerDIV
            className={cs(
              className,
              'd-flex justify-content-center align-items-center flex-column'
            )}
            data-thumb={imageURL}
            tabIndex={tabIndex}
            numberOfShownSlides={isXLScreen ? numberOfShownSlides : null}
          >
            <img src={safeCdnImageRef(imageURL, MEDIA_URL_READ, 'large')} />
          </StyledImageContainerDIV>
        );
      } else if (isMediaVideo(image)) {
        return (
          <StyledImageContainerDIV
            className={cs(
              className,
              'd-flex justify-content-center align-items-center flex-column'
            )}
            data-thumb={image.embed_thumb}
            tabIndex={tabIndex}
            numberOfShownSlides={isXLScreen ? numberOfShownSlides : null}
          >
            <EmbedlyVideoThumbnail
              video={image}
              isVisible={slideId === currentSlideId}
            />
          </StyledImageContainerDIV>
        );
      }
    }

    return (
      <StyledImageContainerDIV
        className={cs(
          className,
          'd-flex justify-content-center align-items-center flex-column'
        )}
        tabIndex={tabIndex}
        numberOfShownSlides={isXLScreen ? numberOfShownSlides : null}
      >
        <div
          ref={iframeRef}
          dangerouslySetInnerHTML={markAsSafe(image.embed_html)}
        />
      </StyledImageContainerDIV>
    );
  }

  return (
    <div className="text-warning p-2">
      <i className="far fa-fw fa-exclamation-triangle"></i>
      {gt('Cannot display the content of the embedded element.')}
    </div>
  );
}

const StyledImageContainerDIV = styled(StyledDiffedImageContainerDiv)<{
  numberOfShownSlides: number | null;
}>`
  ${({numberOfShownSlides}) =>
    numberOfShownSlides != null
      ? `
      min-height: ${
        numberOfShownSlides * (CAROUSEL_THUMBNAIL_SIZE + 2 * CAROUSEL_PADDING)
      }px !important;
  `
      : ''}

  > img {
    display: inline-block !important;
    max-width: 100%;
  }
  iframe {
    max-width: 100%;
  }
`;
